import { useState } from 'react'
import { MinimalInput } from '@ovasabi/ui-minimal'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { commands } from '../../lib/runtime'
import * as S from './OpsStyle'
import { formatWhen, useOpsVendors, type VendorRow } from './opsData'

/**
 * Kitchen directory for operators. Verification decisions are made on the
 * Verification page, from the kitchen's dossier: an approval must record the
 * inspection behind it, and a one-click status change never did.
 */
export const OpsVendorsPage = () => {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const filter = params.get('filter') ?? ''
  const allVendors = useOpsVendors()
  const [done, setDone] = useState<Record<string, string>>({})
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [actionModal, setActionModal] = useState<{
    type: 'suspend' | 'delete'
    vendor: VendorRow
  } | null>(null)
  const [actionReason, setActionReason] = useState('')

  const vendors = allVendors.filter((v) => {
    const currentVerification = done[v.id] ?? v.verification
    if (currentVerification === 'revoked') return false
    if (filter === 'verified') return currentVerification === 'verified'
    if (filter === 'open') return v.isOpen
    if (filter === 'missing_cuisine') return !v.cuisine
    if (filter === 'missing_area') return !v.area
    return true
  })

  const pending = vendors.filter((v) => ['pending', 'unverified', 'suspended'].includes(done[v.id] ?? v.verification))
  const reviewed = vendors.filter((v) => !['pending', 'unverified', 'suspended'].includes(done[v.id] ?? v.verification))

  const handleConfirmAction = async () => {
    if (!actionModal) return
    const { type, vendor } = actionModal
    const trimmed = actionReason.trim()
    if (!trimmed) {
      setError(`A reason is required to ${type} a kitchen.`)
      return
    }
    setBusy(true)
    setError('')
    try {
      if (type === 'suspend') {
        await commands.verification.suspendKitchen(vendor.id, trimmed)
        setDone((prev) => ({ ...prev, [vendor.id]: 'suspended' }))
      } else {
        await commands.verification.deleteKitchen(vendor.id, trimmed)
        setDone((prev) => ({ ...prev, [vendor.id]: 'revoked' }))
      }
      setActionModal(null)
      setActionReason('')
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${type} kitchen.`)
    } finally {
      setBusy(false)
    }
  }

  const row = (vendor: VendorRow) => {
    const verification = done[vendor.id] ?? vendor.verification
    return (
      <tr key={vendor.id}>
        <td>{vendor.displayName}</td>
        <td>{vendor.vendorType.replace(/_/g, ' ')}</td>
        <td>{vendor.cuisine || <S.Pill $tone="warn">not set</S.Pill>}</td>
        <td>{vendor.area || <S.Pill $tone="warn">not set</S.Pill>}</td>
        <td>
          <S.Pill $tone={verification === 'verified' ? 'ok' : verification === 'rejected' ? 'bad' : 'warn'}>
            {verification}
          </S.Pill>
        </td>
        <td>
          <S.Pill $tone={vendor.isOpen ? 'ok' : 'flat'}>{vendor.isOpen ? 'open' : 'closed'}</S.Pill>
        </td>
        <td>{formatWhen(vendor.createdAt)}</td>
        <td>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {/* Verification always goes through the dossier: an approval must
                record an inspection, which a one-click status change never did. */}
            <S.Action onClick={() => navigate(`/ops/verification?kitchen=${encodeURIComponent(vendor.id)}`)}>
              Open dossier
            </S.Action>
            {verification !== 'suspended' && verification !== 'revoked' && (
              <S.Danger
                disabled={busy}
                onClick={() => {
                  setActionModal({ type: 'suspend', vendor })
                  setActionReason('')
                  setError('')
                }}
              >
                Suspend
              </S.Danger>
            )}
            {verification !== 'revoked' && (
              <S.Danger
                disabled={busy}
                onClick={() => {
                  setActionModal({ type: 'delete', vendor })
                  setActionReason('')
                  setError('')
                }}
              >
                Delete
              </S.Danger>
            )}
          </div>
        </td>
      </tr>
    )
  }

  const table = (rows: readonly VendorRow[], emptyText: string) =>
    rows.length === 0 ? (
      <S.Empty>{emptyText}</S.Empty>
    ) : (
      <S.TableScroll>
        <S.Table>
          <thead>
            <tr>
              <th>Kitchen</th>
              <th>Type</th>
              <th>Cuisine</th>
              <th>Area</th>
              <th>Verification</th>
              <th>Sign</th>
              <th>Joined</th>
              <th>Review</th>
            </tr>
          </thead>
          <tbody>{rows.map(row)}</tbody>
        </S.Table>
      </S.TableScroll>
    )

  return (
    <>
      <div>
        <S.H1>Kitchens</S.H1>
      </div>
      {filter && <S.Action onClick={() => setParams({})}>Showing {filter.replace(/_/g, ' ')} · Show all kitchens</S.Action>}
      {error && <S.ErrorNote>{error}</S.ErrorNote>}
      {actionModal && (
        <S.Card>
          <S.CardTitle>
            {actionModal.type === 'suspend' ? 'Suspend kitchen' : 'Delete kitchen'}: {actionModal.vendor.displayName}
          </S.CardTitle>
          <S.Note>
            {actionModal.type === 'suspend'
              ? 'Suspension stops this kitchen from taking orders immediately and requires remediation before reinstatement.'
              : 'Deleting this kitchen permanently revokes selling permissions, unpublishes and archives all menus and dishes. This action cannot be undone if there are no open orders.'}
          </S.Note>
          <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
            <MinimalInput
              label="Reason (required)"
              placeholder={`Why is this kitchen being ${actionModal.type === 'suspend' ? 'suspended' : 'deleted'}?`}
              value={actionReason}
              onChange={(e) => setActionReason(e.target.value)}
            />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <S.Danger disabled={busy || !actionReason.trim()} onClick={handleConfirmAction}>
                {busy ? 'Processing…' : actionModal.type === 'suspend' ? 'Confirm suspension' : 'Confirm deletion'}
              </S.Danger>
              <S.Action disabled={busy} onClick={() => { setActionModal(null); setActionReason(''); setError(''); }}>
                Cancel
              </S.Action>
            </div>
          </div>
        </S.Card>
      )}
      <S.Card>
        <S.CardTitle>Not selling ({pending.length})</S.CardTitle>
        {table(pending, 'Every kitchen is verified.')}
      </S.Card>
      <S.Card>
        <S.CardTitle>Reviewed ({reviewed.length})</S.CardTitle>
        {table(reviewed, 'No kitchens reviewed yet.')}
        <S.Note>
          Chefs manage cuisine and kitchen locations from their profile. Changes appear here through the shared kitchen record.
        </S.Note>
      </S.Card>
    </>
  )
}
